import { CsvAsyncButton } from 'components/CsvButtons';
import { flatten } from 'lodash';
import { AffinityDto, GovernanceComplianceClobDto, KosmosReportDto } from 'models/dtoTypes';
import { IFilterField } from '../../models/types';
import { useTranslation } from 'react-i18next';

// Legacy way of generating CSV file, get JSON first, then generate CSV in frontend

const getColourByAffinity = (affinity: AffinityDto | '') => {
  const mapper: {
    [key: string]: string;
  } = {
    negative: 'Red',
    positive: 'Green',
    neutral: 'White',
    warning: 'Amber',
    unscored: 'Grey',
  };
  return mapper[affinity] || 'Grey';
};

const sdgReportColumns = () => {
  const categorie = [
    'No poverty',
    'Zero hunger',
    'Good health and well-being',
    'Quality education',
    'Gender equality',
    'Clean water and sanitation',
    'Affordable and clean energy',
    'Decent work and economic growth',
    'Industry, Innovation and Infrastructure',
    'Reduced inequality',
    'Sustainable cities and communities',
    'Responsible consumption and production',
    'Climate action',
    'Life below water',
    'Life on land',
    'Peace, justice and strong institutions',
    'Partnership for the goals',
  ];

  return categorie.map((label, index) => {
    return {
      label,
      getter: (report: KosmosReportDto) => {
        const sdgItem = (report.sdgReport || []).find(
          (i) => i.title === label || i.sdgNumber === index + 1,
        );
        return getColourByAffinity(sdgItem?.affinity || '');
      },
    };
  });
};

const sustainableFinanceDisclosureRegulationColumns = () => {
  // should have 14 category
  const categories = [
    'GHG, Greenhouse Gas Emissions - by physical location',
    'GHG, Greenhouse Gas Emissions - by products & services',
    'Carbon footprint',
    'GHG intensity of investee companies',
    'Exposure to companies active in the fossil fuel sector',
    'Share of non-renewable energy consumption and production',
    'Energy consumption intensity per high impact climate sector',
    'Activities negatively affecting biodiversity-sensitive areas',
    'Emissions to water',
    'Hazardous waste ratio',
    'Violations of UN Global Compact principles and OECD Guidelines for Multinational Enterprises',
    'Lack of processes and compliance mechanisms to monitor compliance',
    'Unadjusted gender pay gap',
    'SFDR - Board gender diversity',
    'Exposure to controversial weapons',
  ];

  return categories.map((label, index) => {
    return {
      label,
      getter: (report: KosmosReportDto) => {
        const item = (report.sustainableFinanceDisclosureRegulationReport || []).find(
          (i) => i.number === index,
        );
        return getColourByAffinity(item?.affinity || '');
      },
    };
  });
};

const ghgColumns = () => {
  const categories = [
    { name: 'Company Vehicles', scope: 1, subScope: 1 },
    { name: 'Fuel Combustion', scope: 1, subScope: 2 },
    { name: 'Fugitive Emissions', scope: 1, subScope: 3 },
    // scope2
    { name: 'Purchased Electricity, Heat And Steam', scope: 2, subScope: 1 },
    // scope3
    { name: 'Purchased Goods and Services', scope: 3, subScope: 1 },
    { name: 'Business Travel', scope: 3, subScope: 2 },
    { name: 'Employee Commuting', scope: 3, subScope: 3 },
    { name: 'Waste Disposal', scope: 3, subScope: 4 },
    { name: 'Use of Sold Products', scope: 3, subScope: 5 },
    { name: 'Transportation And Distribution', scope: 3, subScope: 6 },
    { name: 'Investments', scope: 3, subScope: 7 },
    { name: 'Leased Assets and Franchises', scope: 3, subScope: 8 },
  ];

  return categories.map((cat) => {
    const { name, scope, subScope } = cat;
    return {
      label: `Scope ${scope} - ${name}`,
      getter: (report: KosmosReportDto) => {
        const item = (report.ghgScopeReport || []).find(
          (i) => i.scope === scope && i.subScope === subScope,
        );
        return getColourByAffinity(item?.affinity || '');
      },
    };
  });
};

const dualUseGoodsColumns = () => {
  const generateColumn = (affinity: AffinityDto) => {
    return {
      label: `Dual use goods ${affinity}`,
      getter: (report: KosmosReportDto) => {
        const list = report.dualUseGoodsReport || [];
        const items = list.filter((i) => i.affinity === affinity);
        if (items.length) {
          return items
            .map((i) => `${i.entitySummaryCode || ''} - ${i.entitySummaryDetail || ''}`)
            .join('; ');
        } else {
          return '0 record identified';
        }
      },
    };
  };
  return ['positive', 'warning', 'negative'].map((a: string) => generateColumn(a as AffinityDto));
};

const dualUseServiceColumns = () => {
  const generateColumn = (affinity: AffinityDto) => {
    return {
      label: `Dual use service ${affinity}`,
      getter: (report: KosmosReportDto) => {
        const list = report.dualUseServicesReport || [];
        const items = list.filter((i) => i.affinity === affinity);
        if (items.length) {
          return items
            .map((i) => `${i.entitySummaryCode || ''} - ${i.entitySummaryDetail || ''}`)
            .join('; ');
        } else {
          return '0 record identified';
        }
      },
    };
  };
  return ['positive', 'warning', 'negative'].map((a: string) => generateColumn(a as AffinityDto));
};

const illegalLoggingColumns = () => {
  const generateColumn = (affinity: AffinityDto) => {
    return {
      label: `Illegal logging ${affinity}`,
      getter: (report: KosmosReportDto) => {
        const list = report.illegalLoggingReport || [];
        const items = list.filter((i) => i.affinity === affinity);
        if (items.length) {
          return items
            .map((i) => `${i.entitySummaryCode || ''} - ${i.entitySummaryDetail || ''}`)
            .join('; ');
        } else {
          return '0 record identified';
        }
      },
    };
  };
  return ['negative', 'warning'].map((a: string) => generateColumn(a as AffinityDto));
};

// Do No Significant Harm(DNSH)
const dnshColumns = () => {
  const categorie = [
    'Climate Mitigation',
    'Climate Adaptation', // Old report called Climate Adaption, need rescore to show in CSV
  ];
  return categorie.map((label) => {
    return {
      label,
      getter: (report: KosmosReportDto) => {
        const doesNoSignificantHarmItem = (report.doesNoSignificantHarmReport || []).find(
          (i) => i.title === label,
        );
        return getColourByAffinity(doesNoSignificantHarmItem?.affinity || '');
      },
    };
  });
};

const governanceReportColumns = () => {
  const categorie = [
    'Board Gender Diversity',
    'Company Sanctions',
    'Sanctions against directors',
    'Dual Use Goods',
    'Website security risk',
  ];
  return categorie.map((label) => {
    return {
      label,
      getter: (report: KosmosReportDto) => {
        const { PositiveGovernance = [], NegativeGovernance = [] } = report.governanceReport || {};
        const list = flatten([PositiveGovernance, NegativeGovernance]);
        const filtered = list.filter((i) => i.type === label);
        const getMessage = (item: GovernanceComplianceClobDto) => {
          const title = item.title || '';
          const detail = item.detail;
          if (detail?.length) {
            return `${title} ${detail.join(', ')}`;
          } else {
            return title;
          }
        };
        return filtered.map(getMessage).join('; ');
      },
    };
  });
};

const adverseNewsColumns = () => {
  const categories = ['Adverse News'];
  return categories.map((label) => {
    return {
      label,
      getter: (report: KosmosReportDto) => {
        const adverseNews = report.adverseNewsReport ?? [];
        let adverseNewsCount = 0;
        adverseNews.forEach((a) => {
          adverseNewsCount += a.count;
        });
        return adverseNewsCount;
      },
    };
  });
};

const defaultColumns = [
  {
    label: 'Company Name',
    getter: (report: KosmosReportDto) => report.companyMetadata?.name || '',
  },
  {
    label: 'Country Code',
    getter: (report: KosmosReportDto) => report.atlasReport?.countryCode || '',
  },
  {
    label: 'Overall ESG Score',
    getter: (report: KosmosReportDto) => report.kosmosScore || 0,
  },
  {
    label: 'E Score',
    getter: (report: KosmosReportDto) => report.kosmosEsgEnvironmentScore || 0,
  },
  {
    label: 'S Score',
    getter: (report: KosmosReportDto) => report.kosmosEsgSocialScore || 0,
  },
  {
    label: 'G Score',
    getter: (report: KosmosReportDto) => report.kosmosEsgGovernanceScore || 0,
  },
];

const columns = [
  ...defaultColumns,
  ...sdgReportColumns(), // 17 sdg items
  ...dualUseGoodsColumns(),
  ...dualUseServiceColumns(),
  ...illegalLoggingColumns(),
  ...dnshColumns(),
  ...sustainableFinanceDisclosureRegulationColumns(),
  ...ghgColumns(),
  ...governanceReportColumns(),
  ...adverseNewsColumns(),
];

interface IProps {
  fileName?: string;
  onDownloadReports: () => Promise<KosmosReportDto[]>;
  filters?: Omit<IFilterField & { value: string }, 'widget'>[];
}

/**
 *
 * @deprecated use BulkCsvDownloadButton.tsx
 */
export default function ExportKosmosReportsCsvLink(props: IProps) {
  const { onDownloadReports, fileName, filters } = props;
  const { t } = useTranslation('default');
  const handleLoadData = async (): Promise<string[][]> => {
    const data = [];
    const reports = await onDownloadReports();
    const headers = columns.map((i) => i.label);

    if (Array.isArray(filters) && filters.length) {
      const filtersHeaders = filters.map((filter) => filter.label);
      const filtersRow = filters.map((filter) => filter.value);

      data.push(filtersHeaders, filtersRow);
      data.push(['']);
    }

    data.push(headers);
    reports.forEach((report) => {
      const row = columns.map((i) => String(i.getter(report)));
      data.push(row);
    });
    return data;
  };

  return (
    <CsvAsyncButton text={t('export-to-csv')} fileName={fileName} onLoadData={handleLoadData} />
  );
}
